"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Boxes, Cpu, Layers, Sparkles, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardFooter, CardTitle } from "@/components/ui/card";
type Language = "vi" | "en";
interface ProgramCardItem {
  id: string;
  code: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  to: string;
  title: {
    vi: string;
    en: string;
  };
  description: {
    vi: string;
    en: string;
  };
  tag: {
    vi: string;
    en: string;
  };
  features: {
    vi: string[];
    en: string[];
  };
}
const FOCUSED_PROGRAMS: ProgramCardItem[] = [{
  id: "lego-robotics",
  code: "PROGRAM // LEGO",
  icon: Boxes,
  to: "/ProgramsPage",
  title: {
    vi: "Chương Trình LEGO Robotics",
    en: "LEGO Robotics Program"
  },
  description: {
    vi: "Tiếp cận thế giới chế tạo với bộ học cụ LEGO chuyên dụng, làm chủ cơ cấu truyền động bánh răng, đòn bẩy và tư duy thuật toán trực quan sinh động.",
    en: "Hands-on engineering with specialized LEGO robotics kits, mastering mechanical gearing, linkages, and intuitive algorithmic logic."
  },
  tag: {
    vi: "Khám phá & Sáng tạo",
    en: "Discovery & Construction"
  },
  features: {
    vi: ["Mô hình chuyển động & động cơ cơ bản", "Tư duy lập trình khối trực quan", "Phù hợp lứa tuổi khám phá ban đầu"],
    en: ["Kinematic mechanisms & physical builds", "Visual block-based logical sequencing", "Age-tailored exploration for young innovators"]
  }
}, {
  id: "robotics-pathway",
  code: "PATHWAY // D1–HP6",
  icon: Cpu,
  to: "/RoboticsLearningPathwayPage",
  title: {
    vi: "Lộ Trình Robotics Chuẩn Hóa",
    en: "Robotics Learning Pathway"
  },
  description: {
    vi: "Lộ trình đào tạo liên tục từ giai đoạn nền tảng Duplo D1–D4 đến hệ thống điều khiển, cảm biến và lập trình thực chiến WhalesBot HP1–HP6 tiến tới đấu trường ENJOY AI.",
    en: "Continuous curriculum spanning Duplo D1–D4 foundation through WhalesBot HP1–HP6 hardware, smart sensors, and ENJOY AI competition readiness."
  },
  tag: {
    vi: "Duplo D1–D4 & WhalesBot HP1–HP6",
    en: "Duplo D1–D4 & WhalesBot HP1–HP6"
  },
  features: {
    vi: ["Giai đoạn nền tảng: Duplo D1–D4 phát triển tư duy không gian", "Giai đoạn cốt lõi: WhalesBot HP1–HP6 làm chủ mạch & lập trình", "Sẵn sàng thi đấu các giải đấu kỹ thuật quốc tế ENJOY AI"],
    en: ["Foundation Stage: Duplo D1–D4 spatial & mechanical intuition", "Core Robotics: WhalesBot HP1–HP6 microcontrollers & sensors", "Direct preparation for ENJOY AI national & global arenas"]
  }
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export default function NotJustRobotics() {
  const [lang, setLang] = useState<Language>("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage access failures
    }
    const handleCustomLang = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      if (customEvent.detail === "en" || customEvent.detail === "vi") {
        setLang(customEvent.detail);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "en" || event.newValue === "vi")) {
        setLang(event.newValue as Language);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLang);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLang);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const t = {
    specEyebrow: lang === "vi" ? "CHƯƠNG TRÌNH & LỘ TRÌNH ĐÀO TẠO" : "CORE PROGRAMS & LEARNING PATHWAY",
    title: lang === "vi" ? "LEGO Robotics & Lộ Trình Robotics Chuẩn Hóa" : "LEGO Robotics & The Robotics Learning Pathway",
    subtitle: lang === "vi" ? "Khám phá chương trình LEGO Robotics thực nghiệm và lộ trình đào tạo bài bản từ giai đoạn nền tảng Duplo D1–D4 đến hệ thống WhalesBot HP1–HP6 chuyên sâu tại ROBOTO." : "Discover our hands-on LEGO Robotics program and the structured pathway advancing from Duplo D1–D4 foundations through comprehensive WhalesBot HP1–HP6 engineering.",
    closingNote: lang === "vi" ? "Lưu ý dành cho phụ huynh: Chương trình LEGO Robotics và Lộ trình đào tạo Duplo D1–D4, WhalesBot HP1–HP6 được thiết kế khoa học theo từng độ tuổi và năng lực, giúp học sinh xây dựng bản lĩnh kỹ thuật vững chắc." : "Parent Guidance: The LEGO Robotics program and the Duplo D1–D4, WhalesBot HP1–HP6 learning pathway are methodically tailored to age stages, establishing sound engineering intuition and problem-solving readiness.",
    viewAllAction: lang === "vi" ? "Xem tất cả chương trình" : "View all programs",
    exploreTrack: lang === "vi" ? "Xem chi tiết chương trình" : "Explore program details",
    viewPathway: lang === "vi" ? "Xem chi tiết lộ trình" : "Explore pathway details"
  };
  return <section id="notjustrobotics" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border/70">
      {/* Spec background grid accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,var(--foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--foreground)_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header & Technical Photograph Showcase Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column: Header copy */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span aria-hidden="true" className="size-1.5 rounded-xs bg-secondary shrink-0 shadow-xs ring-2 ring-secondary/20" />
              <span aria-hidden="true" className="h-px w-8 bg-secondary/80 shrink-0" />
              <span className="font-mono text-xs font-semibold tracking-wider uppercase text-secondary">
                {t.specEyebrow}
              </span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              {t.title}
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {t.subtitle}
            </p>
          </div>

          {/* Right Column: ROBOTO Technical Architecture Photograph Card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-xl border border-blue-900/50 bg-[#080e22] p-4 sm:p-5 shadow-xl text-white overflow-hidden group">
              {/* Technical Spec Background Grid overlay */}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-15 bg-[linear-gradient(to_right,#3b82f6_1px,transparent_1px),linear-gradient(to_bottom,#3b82f6_1px,transparent_1px)] bg-[size:1.5rem_1.5rem]" />

              {/* Blueprint Header Rail */}
              <div className="relative z-10 flex items-center justify-between pb-3 mb-3 border-b border-blue-800/40 font-mono text-[11px] text-blue-200/90">
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-xs bg-cyan-400 animate-pulse" />
                  <span className="font-bold tracking-widest text-cyan-300 uppercase">
                    {lang === "vi" ? "HỆ THỐNG // CHƯƠNG TRÌNH ROBOTICS" : "SYS.TECH // ROBOTICS CURRICULUM"}
                  </span>
                </div>
                <span className="text-blue-400/80 hidden sm:inline">
                  {lang === "vi" ? "HỌC VIỆN ROBOTO" : "ROBOTO ACADEMY"}
                </span>
              </div>

              {/* High-Resolution Tech Photograph Container */}
              <div className="relative rounded-lg overflow-hidden border border-blue-700/30 bg-blue-950/60 aspect-[16/10] sm:aspect-[16/9] lg:aspect-[4/3] group-hover:border-cyan-400/50 transition-colors duration-300">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_multidisciplinary_stem_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_multidisciplinary_stem_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_multidisciplinary_stem_lab.png-wvc-sizes" alt="ROBOTO academy students actively engaging in LEGO Robotics builds and WhalesBot engineering kits" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" loading="lazy" />

                {/* Dark Deep-Blue Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#080e22] via-[#080e22]/20 to-transparent opacity-85" />

                {/* Floating Technical Diagram Overlay Badges */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  <div className="px-2 py-1 rounded-md bg-[#080e22]/90 backdrop-blur-md border border-cyan-400/40 text-[10px] font-mono text-cyan-200 flex items-center gap-1.5 shadow-sm">
                    <Boxes className="size-3 text-cyan-400" />
                    <span>LEGO ROBOTICS</span>
                  </div>
                  <div className="px-2 py-1 rounded-md bg-[#080e22]/90 backdrop-blur-md border border-blue-400/40 text-[10px] font-mono text-blue-200 flex items-center gap-1.5 shadow-sm">
                    <Layers className="size-3 text-blue-400" />
                    <span>DUPLO & WHALESBOT</span>
                  </div>
                </div>

                {/* Bottom Overlay Label */}
                <div className="absolute bottom-3 left-3 right-3 p-2.5 rounded-lg bg-[#080e22]/95 backdrop-blur-md border border-blue-800/60 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-cyan-400 shrink-0" />
                    <div className="flex flex-col">
                      <span className="font-serif text-xs sm:text-sm font-semibold text-white">
                        LEGO • DUPLO • WHALESBOT
                      </span>
                      <span className="font-mono text-[10px] text-blue-300/80">
                        Duplo D1–D4 & WhalesBot HP1–HP6 System
                      </span>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] font-bold text-cyan-400 bg-cyan-950/80 px-2 py-0.5 rounded border border-cyan-500/30 shrink-0">
                    4–16 YRS
                  </span>
                </div>
              </div>

              {/* Technical Footnote Data Bar */}
              <div className="relative z-10 mt-3 pt-2.5 border-t border-blue-800/30 flex items-center justify-between font-mono text-[10px] text-blue-300/80">
                <span className="flex items-center gap-1.5">
                  <span className="size-1 rounded-xs bg-cyan-400" />
                  <span>Duplo D1–D4 / WhalesBot HP1–HP6 / ENJOY AI</span>
                </span>
                <span className="text-cyan-300/90 font-semibold">ACADEMY LAB STANDARD</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2 Focused Cards Grid: (a) LEGO Robotics & (b) Robotics Pathway */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {FOCUSED_PROGRAMS.map((item, i) => {
          const IconComponent = item.icon;
          const isPathway = item.id === "robotics-pathway";
          return <motion.div key={item.id} data-index={i} variants={cardVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border/80 bg-card/95 hover:border-secondary/70 hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl group relative overflow-hidden p-6 sm:p-8">
                  {/* Subtle top indicator line on hover */}
                  <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-1 bg-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div>
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="size-12 rounded-lg bg-muted/80 border border-border/60 flex items-center justify-center text-primary group-hover:bg-secondary/15 group-hover:text-secondary-foreground transition-colors duration-200">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="font-mono text-[11px] font-medium border-border/70 text-muted-foreground bg-background">
                        {item.code}
                      </Badge>
                    </div>

                    <Badge variant="secondary" className="text-xs w-fit mb-3 font-medium bg-secondary/15 text-secondary-foreground border-transparent px-3 py-1">
                      {item.tag[lang]}
                    </Badge>

                    <CardTitle className="font-serif text-2xl sm:text-3xl text-foreground font-semibold group-hover:text-primary transition-colors duration-200">
                      {item.title[lang]}
                    </CardTitle>

                    <CardDescription className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                      {item.description[lang]}
                    </CardDescription>

                    {/* Stage Feature Highlights */}
                    <div className="mt-6 pt-5 border-t border-border/50 space-y-2.5">
                      <div className="font-mono text-[11px] font-semibold text-secondary uppercase tracking-wider flex items-center gap-1.5">
                        <Compass className="size-3.5" />
                        <span>{lang === "vi" ? "ĐIỂM NỔI BẬT ĐÀO TẠO" : "KEY TRAINING HIGHLIGHTS"}</span>
                      </div>
                      <ul className="space-y-2">
                        {item.features[lang].map((feature, fIdx) => <li key={fIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/85 leading-normal">
                            <span aria-hidden="true" className="size-1.5 rounded-xs bg-secondary shrink-0 mt-1.5" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  <CardFooter className="p-0 pt-6 border-t border-border/50 mt-6 flex items-center justify-between">
                    <Link to={item.to} className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-primary hover:text-primary/80 transition-colors group/link">
                      <span>{isPathway ? t.viewPathway : t.exploreTrack}</span>
                      <ArrowRight className="size-4.5 transition-transform group-hover/link:translate-x-1.5" />
                    </Link>
                    <span className="font-mono text-xs text-muted-foreground/70 hidden sm:inline">
                      {isPathway ? "Robotics Pathway" : "Programs"}
                    </span>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing note for parents & catalog CTA */}
        <div className="mt-14 pt-8 border-t border-border/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-card/60 rounded-xl p-6 md:p-8">
          <div className="max-w-2xl">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t.closingNote}
            </p>
          </div>

          <div className="shrink-0 w-full md:w-auto">
            <Button asChild variant="default" size="lg" className="w-full md:w-auto font-medium shadow-sm hover:shadow">
              <Link to="/ProgramsPage" className="inline-flex items-center gap-2">
                <span>{t.viewAllAction}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}